/**
 * storedAccountAdapter — un compte gardé sur l'appareil (redux `authTokens.storedTokens`) →
 * `UIStoredAccount`, la tuile de « Qui utilise Masteur ? ».
 *
 * Le visage suit la règle de l'ancien `AvatarM` (`common/components/avatar/AvatarM.js`), qui dessine
 * la tuile d'aujourd'hui : l'image est `avatar`, passé par `cleanMediaUrl` (le domaine de dev réécrit),
 * et rien d'autre — ni `avatar_selected`, ni `title_skill_set_id` (la couleur du champion, que la
 * capsule du design system ne porte plus). Sans image, la tuile retombe sur l'initiale. Tuteur =
 * `hasRole(user, "tutor")`, le même test qu'`AvatarM` (un candidat tuteur n'a pas la capsule).
 *
 * `parentChildAdapter` lit les mêmes comptes pour la coquille parent, mais passe `avatar` tel quel,
 * sans `cleanMediaUrl` et sans le rôle : il ne sert pas ici.
 *
 * Le jeton du compte ne sort pas d'ici : l'écran le relit dans le store au moment du geste, par l'id.
 */
import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { hasRole } from "../../../../../data/utility/ability"
import { type UIStoredAccount } from "../../../ui/types"

/** Forme lâche : le compte vient d'un réducteur en JS, tous les champs sont optionnels. */
export type ApiStoredAccount = {
    userId?: string | number
    email?: string
    token?: string
    firstname?: string
    displayName?: string
    roles?: string[]
    avatar?: string | null
    lastConnected?: string
}

/** Une ligne sans `userId` est écartée : sans identité, la tuile ne saurait ni s'ouvrir ni se retirer. */
export function toUIStoredAccount(account: ApiStoredAccount | null | undefined): UIStoredAccount | null {
    if (account?.userId == null) return null
    const avatarUrl = account.avatar ? (cleanMediaUrl(account.avatar) ?? undefined) : undefined
    return {
        id: account.userId,
        firstname: account.firstname ?? "",
        ...(avatarUrl ? { avatarUrl } : {}),
        ...(hasRole(account, "tutor") ? { isTutor: true } : {}),
    }
}

export function toUIStoredAccounts(accounts: (ApiStoredAccount | null | undefined)[]): UIStoredAccount[] {
    return accounts
        .map((account) => toUIStoredAccount(account))
        .filter((account): account is UIStoredAccount => account != null)
}
